/**
 * Move validation
 */
import { getAllAugmentationIds } from "../augmentations/registry";
import type { MoveAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { ChessState } from "../types/game-state";
import {
  AUGMENTATION_CHOICES_PER_ROUND,
  AUGMENTATION_TRIGGER_TURNS,
} from "../utils/augmentation-schedule";
import { getPieceById } from "../utils/board";
import { generateLegalMoves } from "../utils/moves";
import { squaresEqual } from "../utils/square";

/**
 * Validate a move action
 * Returns ValidationResult with valid=true if the move is legal
 */
export function validateMove(
  state: ChessState,
  action: MoveAction,
): ValidationResult {
  // 1. Check game phase
  if (state.phase !== "PLAYING") {
    return {
      valid: false,
      error: "게임이 진행 중이 아닙니다",
      details: { phase: state.phase },
    };
  }

  // 2. Check piece exists
  const piece = getPieceById(state, action.pieceId);
  if (!piece) {
    return {
      valid: false,
      error: "기물을 찾을 수 없습니다",
      details: { pieceId: action.pieceId },
    };
  }

  // 3. Check piece belongs to current player
  if (piece.color !== state.currentPlayer) {
    return {
      valid: false,
      error: "자신의 기물이 아닙니다",
      details: {
        pieceColor: piece.color,
        currentPlayer: state.currentPlayer,
      },
    };
  }

  // 4. Check from position matches piece position
  if (!squaresEqual(piece.position, action.from)) {
    return {
      valid: false,
      error: "기물이 지정된 위치에 없습니다",
      details: {
        piecePosition: piece.position,
        actionFrom: action.from,
      },
    };
  }

  // 5. Check move is in legal moves list
  const legalMoves = generateLegalMoves(state, piece);
  const matchingMove = legalMoves.find((m) => squaresEqual(m.to, action.to));

  if (!matchingMove) {
    return {
      valid: false,
      error: "잘못된 이동입니다",
      details: {
        from: action.from,
        to: action.to,
        legalMovesCount: legalMoves.length,
      },
    };
  }

  // 6. Verify capture consistency (if claimed)
  if (action.capturedPieceId) {
    if (!matchingMove.isCapture) {
      return {
        valid: false,
        error: "잡기 이동이 아닙니다",
        details: { to: action.to },
      };
    }
    if (matchingMove.capturedPieceId !== action.capturedPieceId) {
      return {
        valid: false,
        error: "잡힌 기물이 일치하지 않습니다",
        details: {
          expected: matchingMove.capturedPieceId,
          actual: action.capturedPieceId,
        },
      };
    }
  }

  // 7. Validate triggeredAugmentationChoices
  if (action.triggeredAugmentationChoices) {
    // Reject choices on non-trigger moves
    const isTriggerMove = isMoveAugmentationTrigger(state, piece.color);
    if (!isTriggerMove) {
      return {
        valid: false,
        error: "이 이동은 증강 라운드를 시작하지 않습니다",
        details: {
          turnNumber: state.turnNumber,
          augmentationRound: state.augmentationRound,
        },
      };
    }

    const choicesResult = validateTriggeredAugmentationChoices(
      action.triggeredAugmentationChoices,
    );
    if (!choicesResult.valid) return choicesResult;
  }

  return { valid: true };
}

/**
 * Check whether this move will trigger a mid-game augmentation round.
 * Only Black's move at the turn before a trigger boundary fires the round.
 */
function isMoveAugmentationTrigger(
  state: ChessState,
  movingColor: string,
): boolean {
  if (movingColor !== "BLACK") return false;
  const nextTurn = state.turnNumber + 1;
  const triggerTurn = AUGMENTATION_TRIGGER_TURNS[state.augmentationRound];
  return triggerTurn !== undefined && nextTurn === triggerTurn;
}

/**
 * Validate pre-rolled augmentation choices attached to a triggering move.
 * Ensures all IDs exist in the registry, correct count, and no duplicates.
 */
function validateTriggeredAugmentationChoices(choices: {
  readonly whiteChoices: string[];
  readonly blackChoices: string[];
}): ValidationResult {
  const validIds = getAllAugmentationIds();

  for (const side of ["whiteChoices", "blackChoices"] as const) {
    const ids = choices[side];
    if (ids.length !== AUGMENTATION_CHOICES_PER_ROUND) {
      return {
        valid: false,
        error: "증강 선택지 수가 올바르지 않습니다",
        details: {
          side,
          expected: AUGMENTATION_CHOICES_PER_ROUND,
          actual: ids.length,
        },
      };
    }
    if (new Set(ids).size !== ids.length) {
      return {
        valid: false,
        error: "증강 선택지에 중복이 있습니다",
        details: { side, ids },
      };
    }
    for (const id of ids) {
      if (!validIds.includes(id)) {
        return {
          valid: false,
          error: "잘못된 증강 ID입니다",
          details: { side, invalidId: id },
        };
      }
    }
  }

  return { valid: true };
}
